import type { WorkType } from "@/mocks/works";
import { roleTypes, workYears } from "@/mocks/works";

export type SortKey = "latest" | "popular";

interface FilterPanelProps {
  type: WorkType | "all";
  year: number | "all";
  role: string;
  sort: SortKey;
  onTypeChange: (value: WorkType | "all") => void;
  onYearChange: (value: number | "all") => void;
  onRoleChange: (value: string) => void;
  onSortChange: (value: SortKey) => void;
  onReset: () => void;
}

const chipBase =
  "cursor-pointer whitespace-nowrap rounded-md border px-3 py-1.5 text-xs transition-colors";
const chipActive = "border-primary-600 bg-primary-600 text-background-50";
const chipIdle =
  "border-background-300/70 bg-background-50 text-foreground-700 hover:border-primary-300 hover:text-primary-700";

const typeOptions: { value: WorkType | "all"; label: string }[] = [
  { value: "all", label: "全部类型" },
  { value: "movie", label: "电影" },
  { value: "series", label: "电视剧" },
];

const sortOptions: { value: SortKey; label: string }[] = [
  { value: "latest", label: "按上映时间（最新）" },
  { value: "popular", label: "按热度（高到低）" },
];

const roleOptions = ["全部角色", ...roleTypes];

export default function FilterPanel({
  type,
  year,
  role,
  sort,
  onTypeChange,
  onYearChange,
  onRoleChange,
  onSortChange,
  onReset,
}: FilterPanelProps) {
  return (
    <div className="rounded-lg border border-background-200/80 bg-background-50 p-4 md:p-5">
      <div className="flex flex-col gap-5">
        <div>
          <p className="mb-2.5 text-[11px] uppercase tracking-wider2 text-foreground-400">作品类型</p>
          <div className="flex flex-wrap gap-2">
            {typeOptions.map((option) => (
              <button
                key={option.value}
                type="button"
                onClick={() => onTypeChange(option.value)}
                className={`${chipBase} ${type === option.value ? chipActive : chipIdle}`}
                aria-pressed={type === option.value}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>

        <div>
          <p className="mb-2.5 text-[11px] uppercase tracking-wider2 text-foreground-400">上映年份</p>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => onYearChange("all")}
              className={`${chipBase} ${year === "all" ? chipActive : chipIdle}`}
              aria-pressed={year === "all"}
            >
              全部年份
            </button>
            {workYears.map((item) => (
              <button
                key={item}
                type="button"
                onClick={() => onYearChange(item)}
                className={`${chipBase} ${year === item ? chipActive : chipIdle}`}
                aria-pressed={year === item}
              >
                {item}
              </button>
            ))}
          </div>
        </div>

        <div>
          <p className="mb-2.5 text-[11px] uppercase tracking-wider2 text-foreground-400">角色类型</p>
          <div className="flex flex-wrap gap-2">
            {roleOptions.map((option) => {
              const value = option === "全部角色" ? "all" : option;
              return (
                <button
                  key={option}
                  type="button"
                  onClick={() => onRoleChange(value)}
                  className={`${chipBase} ${role === value ? chipActive : chipIdle}`}
                  aria-pressed={role === value}
                >
                  {option}
                </button>
              );
            })}
          </div>
        </div>

        <div className="flex flex-col gap-3 border-t border-background-200/70 pt-4 sm:flex-row sm:items-center sm:justify-between">
          <label className="flex items-center gap-2 text-xs text-foreground-600">
            <i className="ri-sort-desc text-base" aria-hidden="true"></i>
            排序方式
            <select
              value={sort}
              onChange={(event) => onSortChange(event.target.value as SortKey)}
              className="cursor-pointer rounded-md border border-background-300/70 bg-background-50 px-2.5 py-1.5 text-xs text-foreground-800 outline-none focus:border-primary-400"
            >
              {sortOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>
          <button
            type="button"
            onClick={onReset}
            className="inline-flex w-fit cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md px-2 py-1.5 text-xs text-foreground-600 transition-colors hover:bg-background-100 hover:text-accent-700"
          >
            <i className="ri-refresh-line" aria-hidden="true"></i>
            重置筛选
          </button>
        </div>
      </div>
    </div>
  );
}